import React, { useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import type { GoalChecklistItem, GoalChecklistItemStatus, GoalChecklistSnapshot } from './goalChecklist';
import './GoalChecklistPanel.css';

const STORAGE_KEY = 'adminGoalChecklistExpanded:v1';
const STATUS_LABELS: Record<GoalChecklistItemStatus, string> = {
  completed: 'Complete',
  active: 'Talking now',
  partial: 'Partially addressed',
  pending: 'Not started',
  deferred: 'Deferred',
  blocked: 'Blocked',
};
const STATUS_ICONS: Record<GoalChecklistItemStatus, string> = {
  completed: '✓',
  active: '•',
  partial: '◐',
  pending: '',
  deferred: 'Ⅱ',
  blocked: '!',
};

function savedExpanded(defaultExpanded: boolean): boolean {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === null ? defaultExpanded : saved === '1';
  } catch {
    return defaultExpanded;
  }
}

/** Engines that emit `required` win; older snapshots fall back to the `optional` flag. */
function isRequired(item: GoalChecklistItem): boolean {
  return item.required ?? !item.optional;
}

/** Items the observer needs without asking: required ones, and anything the session has touched. */
function touched(item: GoalChecklistItem): boolean {
  return item.status !== 'pending';
}

function hasDetail(item: GoalChecklistItem): boolean {
  // Counters alone ("0 attempts · confidence 0%") are not worth a click.
  return Boolean(item.description || (item.masteryCriteria && item.masteryCriteria.length > 0) || item.latestEvidence
    || (typeof item.attemptCount === 'number' && item.attemptCount > 0));
}

/** Capability ids are trimmed strings and may contain spaces; keep DOM ids safe. */
function detailDomId(itemId: string): string {
  return `goal-checklist-detail-${encodeURIComponent(itemId)}`;
}

function ItemDetail({ item }: { item: GoalChecklistItem }) {
  const attempts = typeof item.attemptCount === 'number' ? item.attemptCount : null;
  const confidence = typeof item.confidence === 'number' ? Math.round(item.confidence * 100) : null;
  const evidenceMeta = item.latestEvidence
    ? [item.latestEvidence.source, item.latestEvidence.turnId].filter((part): part is string => Boolean(part)).join(' · ')
    : '';
  return (
    <div className="goal-checklist-detail">
      {item.description && <p className="goal-checklist-detail-description">{item.description}</p>}
      {item.masteryCriteria && item.masteryCriteria.length > 0 && (
        <div className="goal-checklist-detail-block">
          <span className="goal-checklist-detail-heading">What counts as evidence</span>
          <ul className="goal-checklist-detail-criteria">
            {item.masteryCriteria.map((criterion, index) => <li key={index}>{criterion}</li>)}
          </ul>
        </div>
      )}
      {(attempts !== null || confidence !== null) && (
        <p className="goal-checklist-detail-meta">
          {attempts !== null && <span>{attempts} {attempts === 1 ? 'attempt' : 'attempts'}</span>}
          {attempts !== null && confidence !== null && <span aria-hidden="true"> · </span>}
          {confidence !== null && <span>confidence {confidence}%</span>}
        </p>
      )}
      {item.latestEvidence ? (
        <div className="goal-checklist-detail-block">
          <span className="goal-checklist-detail-heading">
            Latest evidence{evidenceMeta ? ` · ${evidenceMeta}` : ''}
          </span>
          {item.latestEvidence.learnerExcerpt && (
            <blockquote className="goal-checklist-detail-evidence">{item.latestEvidence.learnerExcerpt}</blockquote>
          )}
          <p className="goal-checklist-detail-rationale">
            <span className="goal-checklist-detail-rationale-label">Why credited</span> {item.latestEvidence.text}
          </p>
        </div>
      ) : (
        <p className="goal-checklist-detail-meta">No evidence recorded yet.</p>
      )}
    </div>
  );
}

function ItemSummary({ item }: { item: GoalChecklistItem }) {
  return (
    <>
      <span className="goal-checklist-status-icon" aria-hidden="true">{STATUS_ICONS[item.status]}</span>
      <span className="goal-checklist-item-label">{item.label}</span>
      <span className="goal-checklist-item-meta">
        {!isRequired(item) && <span className="goal-checklist-optional">Optional</span>}
        <span className="goal-checklist-status-label">{STATUS_LABELS[item.status]}</span>
      </span>
    </>
  );
}

function ItemRow({
  item,
  open,
  onToggle,
}: {
  item: GoalChecklistItem;
  open: boolean;
  onToggle: (id: string) => void;
}) {
  // Engines without per-item detail (e.g. Discovery/Scout) get a static row:
  // no chevron, nothing to click.
  if (!hasDetail(item)) {
    return (
      <li className="goal-checklist-item" data-status={item.status} data-open="false">
        <div className="goal-checklist-item-row">
          <ItemSummary item={item} />
        </div>
      </li>
    );
  }
  const detailId = detailDomId(item.id);
  return (
    <li className="goal-checklist-item" data-status={item.status} data-open={open ? 'true' : 'false'}>
      <button
        type="button"
        className="goal-checklist-item-row goal-checklist-item-toggle"
        aria-expanded={open}
        {...(open ? { 'aria-controls': detailId } : {})}
        onClick={() => onToggle(item.id)}
      >
        <ItemSummary item={item} />
        <KeyboardArrowDownRoundedIcon className="goal-checklist-item-chevron" aria-hidden="true" />
      </button>
      {open && (
        <div id={detailId} className="goal-checklist-item-detail" role="group" aria-label={`${item.label} detail`}>
          <ItemDetail item={item} />
        </div>
      )}
    </li>
  );
}

function ChecklistBody({ snapshot }: { snapshot: GoalChecklistSnapshot }) {
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [showOptionalUntouched, setShowOptionalUntouched] = useState(false);
  const groups = useMemo(() => {
    const required = snapshot.items.filter(isRequired);
    const optionalTouched = snapshot.items.filter((item) => !isRequired(item) && touched(item));
    const optionalUntouched = snapshot.items.filter((item) => !isRequired(item) && !touched(item));
    return { required, optionalTouched, optionalUntouched };
  }, [snapshot.items]);
  const requiredComplete = groups.required.filter((item) => item.status === 'completed').length;
  const touchedCount = snapshot.items.filter(touched).length;
  const optionalCount = groups.optionalUntouched.length;
  const toggleItem = (id: string) => setOpenItemId((current) => (current === id ? null : id));

  const renderGroup = (heading: string, items: GoalChecklistItem[], key: string) => (
    items.length > 0 && (
      <section className="goal-checklist-group" key={key}>
        <h4 className="goal-checklist-group-heading">{heading}</h4>
        <ul className="goal-checklist-items" aria-label={heading}>
          {items.map((item) => (
            <ItemRow key={item.id} item={item} open={openItemId === item.id} onToggle={toggleItem} />
          ))}
        </ul>
      </section>
    )
  );

  return (
    <div id="admin-goal-checklist-items" className="goal-checklist-body">
      <p className="goal-checklist-summary-line">
        {groups.required.length > 0 && (
          <>
            <span>Required {requiredComplete} of {groups.required.length} complete</span>
            <span aria-hidden="true"> · </span>
          </>
        )}
        <span>{touchedCount} of {snapshot.totalCount} touched this session</span>
        {typeof snapshot.coveragePercentage === 'number' && (
          <>
            <span aria-hidden="true"> · </span>
            <span>{snapshot.coverageLabel ?? 'Material covered'} {Math.round(snapshot.coveragePercentage)}%</span>
          </>
        )}
      </p>
      {renderGroup('Required', groups.required, 'required')}
      {renderGroup('Also covered this session', groups.optionalTouched, 'touched')}
      {optionalCount > 0 && (
        showOptionalUntouched ? (
          <>
            {renderGroup('Optional (not started)', groups.optionalUntouched, 'untouched')}
            <button type="button" className="goal-checklist-more" onClick={() => setShowOptionalUntouched(false)}>
              Hide optional items
            </button>
          </>
        ) : (
          <button type="button" className="goal-checklist-more" onClick={() => setShowOptionalUntouched(true)}>
            Show {optionalCount} optional {optionalCount === 1 ? 'item' : 'items'}
          </button>
        )
      )}
    </div>
  );
}

export function GoalChecklistPanel({
  snapshot,
  defaultExpanded,
}: {
  snapshot: GoalChecklistSnapshot;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(() => savedExpanded(defaultExpanded));
  const [announcement, setAnnouncement] = useState('');
  const completed = snapshot.status === 'completed';
  const hasItems = snapshot.items.length > 0;
  const previousAnnouncementState = useRef({
    goalId: snapshot.goalId,
    activeItemId: snapshot.activeItemId,
    status: snapshot.status,
  });
  const shownExpanded = !completed && hasItems && expanded;
  const activeItem = useMemo(
    () => snapshot.items.find((item) => item.id === snapshot.activeItemId) ?? null,
    [snapshot.activeItemId, snapshot.items],
  );

  useEffect(() => {
    if (completed || !hasItems) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, expanded ? '1' : '0');
    } catch {
      // Presentation only.
    }
  }, [completed, expanded, hasItems]);

  const toggle = () => setExpanded((value) => !value);
  const currentCopy = snapshot.status === 'paused'
    ? 'Goal progress paused'
    : snapshot.status === 'error'
      ? 'Goal progress ended before completion'
    : activeItem
      ? `Current: ${activeItem.label}`
      : hasItems
        ? 'Choosing the next step'
        : 'Waiting for goal data';
  const summaryCopy = completed
    ? `Goal complete · ${snapshot.completedCount} of ${snapshot.totalCount} complete`
    : currentCopy;
  // Engines that report weighted mastery drive the bar in percent; engines that
  // only tally items (e.g. Discovery/Scout) keep the item-count bar.
  const weighted = typeof snapshot.progressPercentage === 'number';
  const masteryLabel = weighted ? (snapshot.masteryLabel ?? 'Mastery') : 'Goal completion';
  const barMax = weighted ? 100 : snapshot.totalCount;
  const barValue = weighted ? Math.round(snapshot.progressPercentage as number) : snapshot.completedCount;
  const barCaption = weighted ? `${barValue}%` : `${snapshot.completedCount} of ${snapshot.totalCount}`;
  // Coverage is a second, independent measure: presented share of the goal's
  // bound nodes. Rendered only when the engine reports it, so item-tally
  // engines (Discovery/Scout) are unaffected.
  const hasCoverage = typeof snapshot.coveragePercentage === 'number';
  const coverageLabel = snapshot.coverageLabel ?? 'Material covered';
  const coverageValue = hasCoverage ? Math.round(snapshot.coveragePercentage as number) : 0;

  useEffect(() => {
    const previous = previousAnnouncementState.current;
    if (previous.status !== 'completed' && completed) {
      setAnnouncement(summaryCopy);
    } else if (!completed && (
      previous.goalId !== snapshot.goalId
      || previous.activeItemId !== snapshot.activeItemId
    )) {
      setAnnouncement(currentCopy);
    } else {
      setAnnouncement('');
    }
    previousAnnouncementState.current = {
      goalId: snapshot.goalId,
      activeItemId: snapshot.activeItemId,
      status: snapshot.status,
    };
  }, [completed, currentCopy, snapshot.activeItemId, snapshot.goalId, snapshot.status, summaryCopy]);

  const headerContent = (
    <>
      <span className="goal-checklist-heading">
        <span className="goal-checklist-kicker">ADMIN - GOAL PROGRESS</span>
        {snapshot.demo && <span className="goal-checklist-demo">DEMO DATA</span>}
      </span>
      <span className="goal-checklist-title">{snapshot.title}</span>
      <span className="goal-checklist-current" aria-hidden="true">{summaryCopy}</span>
      <span
        className="goal-checklist-count"
        aria-hidden="true"
      >
        {weighted ? `${masteryLabel} ${barCaption}` : `${snapshot.completedCount} of ${snapshot.totalCount}`}
      </span>
      {!completed && hasItems && <KeyboardArrowDownRoundedIcon className="goal-checklist-chevron" aria-hidden="true" />}
    </>
  );

  return (
    <section className="goal-checklist-panel" aria-label="Goal progress" data-goal-status={snapshot.status} data-has-items={hasItems ? 'true' : 'false'}>
      {completed || !hasItems ? (
        <div className="goal-checklist-header">{headerContent}</div>
      ) : (
        <button
          type="button"
          className="goal-checklist-header"
          aria-expanded={shownExpanded}
          aria-controls="admin-goal-checklist-items"
          aria-label={shownExpanded ? 'Hide goal checklist' : 'Show goal checklist'}
          onClick={toggle}
        >
          {headerContent}
        </button>
      )}
      <span
        className="goal-checklist-progressbar"
        {...(barMax > 0 ? {
          role: 'progressbar',
          // Item-tally engines keep the original accessible name; a weighted
          // engine names its own measure so the two bars stay distinguishable.
          'aria-label': weighted ? `${masteryLabel} progress` : 'Goal completion',
          'aria-valuemin': 0,
          'aria-valuemax': barMax,
          'aria-valuenow': barValue,
        } : {})}
      >
        {masteryLabel} · {barCaption}
      </span>
      {hasCoverage && (
        <span
          className="goal-checklist-progressbar goal-checklist-progressbar-coverage"
          role="progressbar"
          aria-label={`${coverageLabel} progress`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={coverageValue}
        >
          {coverageLabel} · {coverageValue}%
        </span>
      )}
      <span className="goal-checklist-current-announcement" aria-live="polite">
        {announcement}
      </span>

      {shownExpanded && (
        // Keyed on the goal so open-item and fold state reset with no
        // intermediate render of the previous goal's state.
        <ChecklistBody key={snapshot.goalId} snapshot={snapshot} />
      )}
    </section>
  );
}
